import React, { useEffect, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faPlay, faComment } from "@fortawesome/free-solid-svg-icons";
import ClockFlyout from "./ClockFlyout";
import TaskFlyout from "./TaskFlyout";
import StopTaskButton from "./StopTaskButton";
import NotificationsPanel from "./NotificationsPanel";
import ChatFlyout from "./ChatFlyout";

/**
 * SystemTray — the bottom-right tray of the Webtop taskbar (Windows 11 style).
 *
 * Always-visible: a Start icon (start/switch task), notifications bell, Login
 * time, current Task + task time, and the clock. The Start icon opens a
 * task-picker flyout; the clock opens a calendar + world-clocks flyout. One
 * panel at a time; click-outside / Escape closes it.
 *
 * IMPORTANT (Rule 4): the always-visible durations only MOVE the render — they
 * read timestamps from state.timer and compute (now - ts), kept live by a 1s
 * re-render tick. No edits to Timer.jsx.
 */

const toMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  const n = Number(ts);
  if (!Number.isNaN(n) && String(ts).trim() !== "") return n;
  const parsed = new Date(ts).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatElapsed = (tsMs) => {
  if (!tsMs) return "00:00:00";
  const total = Math.max(0, Math.floor((Date.now() - tsMs) / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${days > 0 ? `${days}d ` : ""}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const clockTime = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const clockDate = (d) => d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });

// antd renders Select/DatePicker dropdowns in body-level portals. A click on one
// must NOT be treated as "outside" the tray (that was closing the task picker
// while choosing a dropdown value).
const PORTAL_SELECTOR =
  ".ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown, .ant-select-item, .rc-virtual-list, .ant-modal";

const SystemTray = () => {
  const timer = useSelector((s) => s.timer) || {};
  const { loginTime, time, task } = timer;
  const isRunning = !!task && task !== "Available";
  const unread = useSelector((s) => s.emailSocket?.unreadCount) || 0;
  const chatUnread = useSelector((s) => s.chat?.totalUnreadCount) || 0;

  const [openPanel, setOpenPanel] = useState(null); // null | "task" | "clock" | "notifications" | "chat"
  const trayRef = useRef(null);

  // Re-render once a second so wall-clock-derived values stay live.
  const [, tick] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Close any open panel on outside click — but ignore antd portal overlays.
  useEffect(() => {
    const onDown = (e) => {
      if (!trayRef.current || trayRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest(PORTAL_SELECTOR)) return;
      setOpenPanel(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const toggle = (panel) => setOpenPanel((cur) => (cur === panel ? null : panel));

  const now = new Date();
  const taskLabel = isRunning ? task : "Available";

  return (
    <div className="webtop-tray" ref={trayRef}>
      {openPanel === "task" && <TaskFlyout onClose={() => setOpenPanel(null)} />}
      {openPanel === "clock" && <ClockFlyout onClose={() => setOpenPanel(null)} />}
      {openPanel === "notifications" && <NotificationsPanel onClose={() => setOpenPanel(null)} />}
      {openPanel === "chat" && <ChatFlyout onClose={() => setOpenPanel(null)} />}

      <button
        type="button"
        className={`webtop-tray__btn webtop-tray__chat${openPanel === "chat" ? " webtop-tray__btn--open" : ""}`}
        title="Messages"
        aria-label="Messages"
        onClick={() => toggle("chat")}
      >
        <FontAwesomeIcon icon={faComment} />
        {chatUnread > 0 && <span className="webtop-tray__badge">{chatUnread > 99 ? "99+" : chatUnread}</span>}
      </button>

      <button
        type="button"
        className={`webtop-tray__btn webtop-tray__bell${openPanel === "notifications" ? " webtop-tray__btn--open" : ""}`}
        title="Notifications"
        aria-label="Notifications"
        onClick={() => toggle("notifications")}
      >
        <FontAwesomeIcon icon={faBell} />
        {unread > 0 && <span className="webtop-tray__badge">{unread > 99 ? "99+" : unread}</span>}
      </button>

      <button
        type="button"
        className={`webtop-tray__btn webtop-tray__start${isRunning ? " webtop-tray__start--running" : ""}${openPanel === "task" ? " webtop-tray__btn--open" : ""}`}
        title={isRunning ? `Working: ${task} — switch task` : "Start a task"}
        aria-label="Start task"
        onClick={() => toggle("task")}
      >
        <FontAwesomeIcon icon={faPlay} />
      </button>

      {isRunning && <StopTaskButton />}

      <div className="webtop-tray__metric" title="Login time">
        <span className="webtop-tray__lbl">Login</span>
        <span className="webtop-tray__val">{formatElapsed(toMs(loginTime))}</span>
      </div>

      <div className="webtop-tray__metric" title={`${taskLabel} time`}>
        <span className="webtop-tray__lbl">{taskLabel}</span>
        <span className="webtop-tray__val">{formatElapsed(toMs(time))}</span>
      </div>

      <button
        type="button"
        className={`webtop-tray__clock webtop-tray__clock--btn${openPanel === "clock" ? " webtop-tray__clock--open" : ""}`}
        title="Calendar and world clocks"
        aria-label="Calendar and world clocks"
        onClick={() => toggle("clock")}
      >
        <span className="webtop-tray__time">{clockTime(now)}</span>
        <span className="webtop-tray__date">{clockDate(now)}</span>
      </button>
    </div>
  );
};

export { formatElapsed, toMs };
export default SystemTray;
